import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Table, Button, Space, Tag, Typography, Input, message, Modal, Form,
  Select, Switch, Popconfirm, Card, Row, Col, Tabs, Radio, Divider,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined, CodeOutlined, FileTextOutlined } from '@ant-design/icons';
import { blogAPI } from '../services/api';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

function HtmlPreview({ html, style }) {
  const ref = React.useRef(null);
  const [height, setHeight] = useState(200);
  const updateHeight = useCallback(() => {
    try {
      const doc = ref.current?.contentDocument || ref.current?.contentWindow?.document;
      if (doc?.body) {
        const h = doc.body.scrollHeight;
        if (h > 50) setHeight(Math.min(h + 24, 500));
      }
    } catch { /* cross-origin */ }
  }, []);

  useEffect(() => {
    const iframe = ref.current;
    if (!iframe) return;
    iframe.addEventListener('load', updateHeight);
    const id = setInterval(updateHeight, 800);
    return () => { iframe.removeEventListener('load', updateHeight); clearInterval(id); };
  }, [updateHeight]);

  const srcDoc = useMemo(() => `<!DOCTYPE html><html><head><meta charset="UTF-8"><style>body{font-family:-apple-system,sans-serif;margin:12px;line-height:1.6;color:#333;}</style></head><body>${html || ''}</body></html>`, [html]);

  return (
    <iframe
      ref={ref}
      srcDoc={srcDoc}
      title="preview"
      style={{ width: '100%', border: '1px solid #d9d9d9', borderRadius: 6, height, transition: 'height 0.3s', ...style }}
    />
  );
}

function ContentEditor({ namePrefix, format, showPreview, t }) {
  const contentField = namePrefix ? `${namePrefix}_content` : 'content';
  const contentFieldName = namePrefix === 'th' ? 'content_th' : 'content';
  const [previewVisible, setPreviewVisible] = useState(false);
  const form = Form.useFormInstance();

  if (format === 'rich') {
    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <Text strong style={{ fontSize: 13 }}>
            <CodeOutlined /> {t('blog.form.htmlEditor')}
          </Text>
          <Button
            size="small"
            type={previewVisible ? 'primary' : 'default'}
            icon={<EyeOutlined />}
            onClick={() => setPreviewVisible(!previewVisible)}
          >
            {t('blog.form.preview')}
          </Button>
        </div>
        <Form.Item name={contentFieldName} rules={namePrefix ? [] : [{ required: true, message: t('blog.form.contentRequired') }]}>
          <TextArea
            rows={12}
            placeholder={t('blog.form.htmlPlaceholder')}
            style={{ fontFamily: 'monospace', fontSize: 12 }}
          />
        </Form.Item>
        {previewVisible && (
          <div style={{ marginTop: -16, marginBottom: 16 }}>
            <Text type="secondary" style={{ fontSize: 12, marginBottom: 4, display: 'block' }}>{t('blog.form.previewLabel')}</Text>
            <Form.Item noStyle shouldUpdate={(prev, cur) => prev[contentFieldName] !== cur[contentFieldName]}>
              {() => <HtmlPreview html={form.getFieldValue(contentFieldName) || ''} />}
            </Form.Item>
          </div>
        )}
      </div>
    );
  }

  return (
    <Form.Item
      name={contentFieldName}
      label={namePrefix === 'th' ? t('blog.form.contentTh') : t('blog.form.content')}
      rules={namePrefix ? [] : [{ required: true, message: t('blog.form.contentRequired') }]}
    >
      <TextArea rows={10} placeholder={namePrefix === 'th' ? t('blog.form.contentThPlaceholder') : t('blog.form.contentPlaceholder')} />
    </Form.Item>
  );
}

export default function BlogListPage() {
  const { t } = useTranslation();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [saving, setSaving] = useState(false);
  const [contentFormat, setContentFormat] = useState('simple');
  const [form] = Form.useForm();
  const pageSize = 20;

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await blogAPI.getPosts({ page, page_size: pageSize });
      setPosts(res.data?.posts || []);
      setTotal(res.data?.total || 0);
    } catch {
      message.error(t('blog.loadError'));
    } finally { setLoading(false); }
  }, [page, t]);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  const openCreate = () => {
    setEditingPost(null);
    setContentFormat('simple');
    form.resetFields();
    form.setFieldsValue({ is_published: false, content_format: 'simple' });
    setModalOpen(true);
  };

  const openEdit = (post) => {
    setEditingPost(post);
    setContentFormat(post.content_format || 'simple');
    form.setFieldsValue({
      title: post.title,
      slug: post.slug,
      excerpt: post.excerpt,
      content: post.content,
      content_format: post.content_format || 'simple',
      title_th: post.title_th,
      excerpt_th: post.excerpt_th,
      content_th: post.content_th,
      featured_image: post.featured_image,
      tags: post.tags,
      is_published: post.is_published,
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const values = await form.validateFields();
      values.content_format = contentFormat;
      if (!values.slug) {
        values.slug = values.title.toLowerCase().replace(/[^a-z0-9\u4e00-\u9fff]+/g, '-').replace(/(^-|-$)/g, '');
      }
      if (editingPost) {
        await blogAPI.updatePost(editingPost.id, values);
        message.success(t('blog.updateSuccess'));
      } else {
        await blogAPI.createPost(values);
        message.success(t('blog.createSuccess'));
      }
      setModalOpen(false);
      form.resetFields();
      fetchPosts();
    } catch (err) {
      if (err.errorFields) message.error(t('common.fillRequired'));
      else message.error(`${t('common.saveFailed')}: ${err.message}`);
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try {
      await blogAPI.deletePost(id);
      message.success(t('blog.deleteSuccess'));
      fetchPosts();
    } catch { message.error(t('common.deleteFailed')); }
  };

  const handleTogglePublish = async (post) => {
    try {
      await blogAPI.updatePost(post.id, { is_published: !post.is_published });
      fetchPosts();
    } catch { message.error(t('common.updateFailed')); }
  };

  const columns = [
    {
      title: t('blog.table.title'), dataIndex: 'title', width: 260,
      render: (v, post) => (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {post.content_format === 'rich'
              ? <Tag color="purple"><CodeOutlined /> {t('blog.table.richFormat')}</Tag>
              : <Tag><FileTextOutlined /> {t('blog.table.simpleFormat')}</Tag>
            }
          </div>
          <div style={{ fontWeight: 500, marginTop: 4 }}>{v}</div>
          {post.excerpt && <Text type="secondary" style={{ fontSize: 12 }}>{post.excerpt.substring(0, 50)}...</Text>}
        </div>
      ),
    },
    {
      title: t('blog.table.tags'), dataIndex: 'tags', width: 130,
      render: tags => tags?.length ? tags.slice(0, 3).map(tag => <Tag key={tag}>{tag}</Tag>) : '-',
    },
    {
      title: t('blog.table.languages'), width: 80, align: 'center',
      render: (_, post) => (
        <Space size={2}>
          <Tag color="blue">🇹🇼</Tag>
          {post.content_th && <Tag color="green">🇹🇭</Tag>}
        </Space>
      ),
    },
    {
      title: t('blog.table.views'), dataIndex: 'view_count', width: 70, align: 'center',
    },
    {
      title: t('blog.table.published'), width: 70, align: 'center',
      render: (_, post) => (
        <Switch size="small" checked={post.is_published} onChange={() => handleTogglePublish(post)} />
      ),
    },
    {
      title: t('blog.table.createdAt'), dataIndex: 'created_at', width: 120,
      render: v => v ? new Date(v).toLocaleDateString('zh-TW') : '-',
    },
    {
      title: t('blog.table.actions'), width: 100,
      render: (_, post) => (
        <Space size="small">
          <Button type="text" size="small" icon={<EditOutlined />} onClick={() => openEdit(post)} />
          <Popconfirm title={t('common.confirmDelete')} onConfirm={() => handleDelete(post.id)} okText={t('common.delete')} cancelText={t('common.cancel')}>
            <Button type="text" size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>{t('blog.pageTitle')}</Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>{t('blog.addButton')}</Button>
      </div>

      <Card bodyStyle={{ padding: 0 }}>
        <Table
          columns={columns}
          dataSource={posts}
          rowKey="id"
          loading={loading}
          pagination={{
            current: page, pageSize, total,
            onChange: p => setPage(p),
            showTotal: (total) => t('blog.paginationTotal', { total }),
          }}
          size="middle"
        />
      </Card>

      <Modal
        title={editingPost ? t('blog.editTitle') : t('blog.addTitle')}
        open={modalOpen}
        onOk={handleSave}
        onCancel={() => { setModalOpen(false); form.resetFields(); }}
        okText={t('common.save')}
        cancelText={t('common.cancel')}
        width={820}
        confirmLoading={saving}
        styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}
      >
        <Form form={form} layout="vertical">
          <Row gutter={16} align="middle">
            <Col span={8}>
              <Form.Item label={t('blog.form.formatLabel')} style={{ marginBottom: 12 }}>
                <Radio.Group
                  value={contentFormat}
                  onChange={e => { setContentFormat(e.target.value); form.setFieldValue('content_format', e.target.value); }}
                  optionType="button"
                  buttonStyle="solid"
                >
                  <Radio.Button value="simple"><FileTextOutlined /> {t('blog.form.formatSimple')}</Radio.Button>
                  <Radio.Button value="rich"><CodeOutlined /> {t('blog.form.formatRich')}</Radio.Button>
                </Radio.Group>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="slug" label={t('blog.form.slug')} style={{ marginBottom: 12 }}>
                <Input placeholder={t('blog.form.slugPlaceholder')} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="tags" label={t('blog.form.tags')} style={{ marginBottom: 12 }}>
                <Select mode="tags" placeholder={t('blog.form.tagsPlaceholder')} />
              </Form.Item>
            </Col>
          </Row>

          <Divider style={{ margin: '4px 0 16px' }} />

          <Tabs
            defaultActiveKey="zh-TW"
            items={[
              {
                key: 'zh-TW',
                label: <span>🇹🇼 {t('blog.form.langZh')}</span>,
                children: (
                  <>
                    <Form.Item name="title" label={t('blog.form.title')} rules={[{ required: true, message: t('blog.form.titleRequired') }]}>
                      <Input placeholder={t('blog.form.titlePlaceholder')} />
                    </Form.Item>
                    <Form.Item name="excerpt" label={t('blog.form.excerpt')}>
                      <Input placeholder={t('blog.form.excerptPlaceholder')} maxLength={300} />
                    </Form.Item>
                    <ContentEditor format={contentFormat} t={t} />
                  </>
                ),
              },
              {
                key: 'th',
                label: <span>🇹🇭 {t('blog.form.langTh')}</span>,
                children: (
                  <>
                    <Form.Item name="title_th" label={t('blog.form.titleTh')}>
                      <Input placeholder={t('blog.form.titleThPlaceholder')} />
                    </Form.Item>
                    <Form.Item name="excerpt_th" label={t('blog.form.excerptTh')}>
                      <Input placeholder={t('blog.form.excerptThPlaceholder')} maxLength={300} />
                    </Form.Item>
                    <ContentEditor namePrefix="th" format={contentFormat} t={t} />
                  </>
                ),
              },
            ]}
          />
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="featured_image" label={t('blog.form.featuredImage')}>
                <Input placeholder="https://..." />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="is_published" label={t('blog.form.publishedLabel')} valuePropName="checked">
                <Switch checkedChildren={t('blog.form.publishedOn')} unCheckedChildren={t('blog.form.publishedOff')} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
}
